// components/attendance/components/AttendanceViewModal.js
import React from 'react';
import Modal from '../../../components/ui/modal';
import Image from '../../../components/appImage';
import Icon from '../../../components/appIcon';
import PPEStatusBadge from '../../../components/ppeStatusBadge';
import PPEItemsList from '../../../components/ppeItemsList'; // We'll create this component

const AttendanceViewModal = ({ detectionMode, isOpen, onClose, record }) => {
  if (!isOpen || !record) return null;

  const BACKEND_URL = import.meta.env.VITE_API_URL;
  
  // Parse missing and detected PPE items if they're strings
  const missingPpeItems = typeof record?.missingPpeItems === 'string' 
    ? JSON.parse(record?.missingPpeItems) 
    : record?.missingPpeItems || [];
    
  const detectedPpeItems = typeof record?.detectedPpeItems === 'string' 
    ? JSON.parse(record?.detectedPpeItems) 
    : record?.detectedPpeItems || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="bg-card rounded-lg shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Calendar" size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Attendance Details</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-muted"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Employee Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-muted rounded-full overflow-hidden">
                  <Image
                    src={`${BACKEND_URL}${record?.employee?.avatar}`}
                    alt={record?.employee?.name}
                    className="object-cover w-full h-full"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">
                    {record?.employee?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    ID: {record?.employee?.id}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {record?.employee?.department?.name}
                  </p>
                </div>
              </div>

              {/* Attendance Info */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Check In</p>
                    <p className="text-sm text-muted-foreground">
                      {record?.checkIn 
                        ? new Date(record.checkIn).toLocaleString()
                        : 'Not checked in'
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Check Out</p>
                    <p className="text-sm text-muted-foreground">
                      {record?.checkOut 
                        ? new Date(record.checkOut).toLocaleString()
                        : 'Not checked out'
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Icon name="MapPin" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Location</p>
                    <p className="text-sm text-muted-foreground">
                      {record?.location || 'Unknown'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Icon name="Activity" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Status</p>
                    <p className="text-sm text-muted-foreground">
                      {record?.status || 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Image & PPE Info */}
            <div className="space-y-4">
              {/* Captured Image */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Captured Image</h4>
                {record?.captureImage ? (
                  <div className="w-full h-96 bg-muted rounded-lg overflow-hidden">
                    <Image
                      src={`${BACKEND_URL}${record?.captureImage}`}
                      alt="Attendance capture"
                      className=" w-full h-full"
                    />
                  </div>
                ) : (
                  <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center">
                    <Icon name="ImageOff" size={24} className="text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* PPE Status */}
              <div>
                {detectionMode === "FULL_KIT" &&
                  <>
                    <h4 className="text-sm font-medium text-foreground mb-2">PPE Compliance</h4>
                    <div className="p-3 bg-muted rounded-lg">
                      <PPEStatusBadge
                        status={record?.ppeStatus}
                        missingItems={missingPpeItems}
                      />

                      {/* Show detected items */}
                      {detectedPpeItems && detectedPpeItems.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-foreground mb-1">Detected PPE:</p>
                          <PPEItemsList items={detectedPpeItems} type="detected" />
                        </div>
                      )}

                      {/* Show missing items */}
                      {missingPpeItems && missingPpeItems.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-foreground mb-1">Missing PPE:</p>
                          <PPEItemsList items={missingPpeItems} type="missing" />
                        </div>
                      )}
                    </div>
                  </>
                }
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AttendanceViewModal;